import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { UserInfo } from 'src/auth/types';
import { refreshMagicToken } from 'src/services/auth/refreshMagicToken';

const STORAGE_KEY = 'magic-link-auth';

interface MagicLinkState {
  accessToken: string | undefined;
  refreshToken: string | undefined;
  user: UserInfo | undefined;
  authenticated: boolean;
  initialized: boolean;
  tokenExpiresAt: Date | undefined;
  actions: {
    initialize: () => Promise<void>;
    login: (token: string) => Promise<void>;
    logout: () => Promise<void>;
    updateToken: (forceRefresh?: boolean) => Promise<boolean>;
    getAccessToken: () => string | undefined;
    setTokens: (
      accessToken: string,
      refreshToken: string,
      user: UserInfo,
      expiresIn: number,
    ) => void;
    clearTokens: () => void;
  };
}

export const useMagicLink = create<MagicLinkState>()(
  persist(
    (set, get) => ({
      accessToken: undefined,
      refreshToken: undefined,
      user: undefined,
      authenticated: false,
      initialized: true, // Start as initialized so useAuth can check state immediately
      // Rehydration will update authenticated state when it completes
      tokenExpiresAt: undefined,
      actions: {
        initialize: async () => {
          // State restoration is primarily handled by onRehydrateStorage
          // This function ensures initialized is set and re-validates token expiration
          // Similar to how Entra auto-detects accounts
          const state = get();
          // Always ensure initialized is true (it should be set in initial state)
          if (!state.initialized) {
            set({ initialized: true });
          }

          // Re-validate token expiration
          if (state.accessToken && state.user && state.tokenExpiresAt) {
            if (state.tokenExpiresAt <= new Date()) {
              // Token expired, clear it
              get().actions.clearTokens();
            } else if (!state.authenticated) {
              // Token is valid but authenticated flag is false, fix it
              set({ authenticated: true });
            }
          } else if (state.authenticated) {
            // No tokens but authenticated is true, fix it
            set({ authenticated: false });
          }
        },
        login: async () => {
          // Login for magic link happens via the callback URL, not through this action
          // This is a no-op to prevent errors when AuthProtectedRoute tries to trigger login
          // If tokens are present, they will be detected automatically
          // If no tokens, user should use the magic link from email
          const state = get();
          if (!state.authenticated && !state.accessToken) {
            // No tokens - this is expected, user needs to use magic link
            // Don't throw error, just return silently
            return;
          }
        },
        logout: async () => {
          // This will call the logout API and then clear tokens
          // The actual API call happens in the logout service
          get().actions.clearTokens();
        },
        updateToken: async () => {
          const state = get();
          if (state.refreshToken) {
            try {
              const result = await refreshMagicToken(state.refreshToken);
              if (result.success && result.accessToken) {
                get().actions.setTokens(
                  result.accessToken,
                  result.refreshToken,
                  result.user,
                  result.expiresIn,
                );
                return true;
              }
            } catch (error) {
              console.error('Failed to refresh magic link token', error);
            }
          }
          return false;
        },
        getAccessToken: () => {
          return get().accessToken;
        },
        setTokens: (
          accessToken: string,
          refreshToken: string,
          user: UserInfo,
          expiresIn: number,
        ) => {
          const validExpiresIn =
            typeof expiresIn === 'number' && !isNaN(expiresIn)
              ? expiresIn
              : 3600;
          const expiresAt = new Date(Date.now() + validExpiresIn * 1000);
          set({
            accessToken,
            refreshToken,
            user,
            authenticated: true,
            initialized: true,
            tokenExpiresAt: expiresAt,
          });
        },
        clearTokens: () => {
          set({
            accessToken: undefined,
            refreshToken: undefined,
            user: undefined,
            authenticated: false,
            tokenExpiresAt: undefined,
          });
        },
      },
    }),
    {
      name: STORAGE_KEY,
      // Only persist accessToken, refreshToken, user, authenticated, and tokenExpiresAt
      // Don't persist actions or initialized
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        authenticated: state.authenticated,
        // Store tokenExpiresAt as timestamp (number) for proper serialization
        tokenExpiresAt:
          state.tokenExpiresAt && !isNaN(state.tokenExpiresAt.getTime())
            ? state.tokenExpiresAt.getTime()
            : undefined,
      }),
      // Use sessionStorage instead of localStorage for better security
      // Tokens are cleared when browser session ends
      storage: createJSONStorage(() => sessionStorage),
      // Convert tokenExpiresAt from timestamp back to Date on restore
      // This runs automatically when the store is hydrated from sessionStorage
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // Convert tokenExpiresAt from timestamp (number) back to Date
        if (state.tokenExpiresAt && typeof state.tokenExpiresAt === 'number') {
          state.tokenExpiresAt = new Date(state.tokenExpiresAt);
        }

        // Auto-initialize and validate tokens if we have them
        // This mimics Entra's automatic account detection
        if (state.accessToken && state.user) {
          // Check if token is expired
          if (state.tokenExpiresAt && state.tokenExpiresAt > new Date()) {
            // Valid token - user is authenticated
            state.initialized = true;
            state.authenticated = true;
          } else {
            // Token expired, clear it
            state.accessToken = undefined;
            state.refreshToken = undefined;
            state.user = undefined;
            state.authenticated = false;
            state.tokenExpiresAt = undefined;
            state.initialized = true;
          }
        } else {
          // No tokens - user is not authenticated but store is initialized
          state.initialized = true;
          state.authenticated = false;
        }
      },
    },
  ),
);
